import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { EditableColumnDef, EditableTable } from "../../../../../shared/components/table/inline-edit-table";
import { CustomSelect, CustomTextField } from "../../../../../shared/components/inputfields/form-elements";
import { StyledButton } from "../../../../../shared/components/buttons/custom-button";
import { FacilityInformation, FacilityProcessDetails } from "../../../sc-models/facility-product-dto";
import { dtrackItService } from "../../../../transaction-certificates/tc-service/dtrackit.service";
import { useFormActions } from "../../../../../shared/hooks/form-builder.hooks";
import { Utills } from "../../../../../shared/app-configs/utilit";
import { useApi } from "../../../../../shared/hooks/useApi";
import { API_ROUTES } from "../../../../../shared/app-configs/sc-app-config";
import { useParams } from "react-router-dom";
import { useTransactionCertificateStore } from "../../../../../store/transaction-certificates.store";

interface RawMaterialsInfoProps {
  facilityData: FacilityInformation;
  facilityProcessInfo: FacilityProcessDetails[]; // Expecting an array
  setFacilityProcessInfo: React.Dispatch<
    React.SetStateAction<FacilityProcessDetails[]>
  >;
  setSubsequentFacilityFlag: React.Dispatch<React.SetStateAction<boolean>>;
  setScSubsequentFacilityButtonFlag: React.Dispatch<React.SetStateAction<boolean>>;
  scNoOfFarmsFlag: boolean;
  setScNoOfFarmsFlag: React.Dispatch<React.SetStateAction<boolean>>;
  subsequentFacilityFlag: boolean;
  scSubsequentFacilityButtonFlag: boolean;
}

const FacilityProcessInfo: React.FC<RawMaterialsInfoProps> = ({
  facilityData,
  facilityProcessInfo,
  setFacilityProcessInfo,
  setSubsequentFacilityFlag,
  setScSubsequentFacilityButtonFlag,
  scNoOfFarmsFlag,
  setScNoOfFarmsFlag,
  subsequentFacilityFlag,
  scSubsequentFacilityButtonFlag
}) => {
  const { updateField } = useFormActions("sc-facilitydetails");
  const [data, setData] = useState<FacilityProcessDetails[]>(
    facilityProcessInfo
  ); // Initialize with prop data
  const [tableData, setTableData] = useState<FacilityProcessDetails[]>(
    facilityProcessInfo
  ); // Initialize with prop data
  const [editingRowIndex, setEditingRowIndex] = useState<number>(-1);
  const [facilityProcessCategoryCode, setFacilityProcessCategoryCode] = useState([]);
  const { executeGet } = useApi()
  const { SC_ID } = useParams()
  const { isRestrictPopUpSave, setRestrictPopUpSave, setFacilityProcessCategoryCodes}=useTransactionCertificateStore()

  const [selectedStandards, setSelectedStandards] = useState<string[]>([]);
  const [showWarning, setShowWarning] = useState(false);
  const [warningMessage, setWarningMessage] = useState<string | null>(null);
  
  // Reset save button state whenever component mounts or editingRowIndex changes
  useEffect(() => {
    if (editingRowIndex === -1) {
      setRestrictPopUpSave(false);
    }
  }, [editingRowIndex]);

  const fetchSCStandardDetails = async () => {
    const result = await executeGet(API_ROUTES.GET_SC_DETAILS, { scId: SC_ID });
    console.log("result", result?.data)
    if (result?.data?.scStandardDetails?.length > 0) {
      const standards = result?.data.scStandardDetails
        .filter((detail: any) =>
          detail.numberOfAuditDays === 0 || detail.scNumberOfFarms === 0
        )
        .map((detail: any) => detail.scStandard);
      // console.log("standards", standards)
      setSelectedStandards(standards);
      const scNoOfFarms = result?.data.scStandardDetails.filter((detail: any) => detail.scNumberOfFarms > 0)
      console.log("scNoOfFarms-->", scNoOfFarms);
      setScNoOfFarmsFlag(scNoOfFarms.length > 0)
    }
    return result;
  };

  const getValidationMessage = (updatedRow: FacilityProcessDetails) => {
    const farmCategories = ["PR0036", "PR0011", "PR0037", "PR0038"];
    const hasFarmCategory = farmCategories.includes(updatedRow.FacilityProccessCategoryCode);

    if (scNoOfFarmsFlag && !hasFarmCategory && !facilityData?.iId) {
      console.log("Condition is true or not", scNoOfFarmsFlag, !hasFarmCategory, facilityData?.iId)
      setSubsequentFacilityFlag(false);
      setScSubsequentFacilityButtonFlag(false);
      return "Non-farm facility processes need TE_ID. Please fill in TE-ID in Facility Information Tab to proceed further";
    } else {
        setSubsequentFacilityFlag(true);
        setScSubsequentFacilityButtonFlag(true);
    }
    if (!hasFarmCategory) return null;

    if (selectedStandards.includes("RDS") && (selectedStandards.includes("RWS") || selectedStandards.includes("RMS") || selectedStandards.includes("RAS"))) {
      return "For RDS/RAF standards with farm categories, please specify the Number of Farms in the Standard Details section.";
    }
    if (selectedStandards.includes("RDS")) {
      return "For RDS standard with farm categories, please specify the Number of Audit Days in the Standard Details section.";
    }
    return null;
  };

  const setSubmissionFlag = (updatedRow: FacilityProcessDetails) => {
    const farmCategories = ["PR0036", "PR0011", "PR0037", "PR0038"];
    const hasFarmCategory = farmCategories.includes(updatedRow.FacilityProccessCategoryCode);
    if (scNoOfFarmsFlag && !hasFarmCategory && facilityData.iId) {
      setScSubsequentFacilityButtonFlag(true);
    }
  }
  
  useEffect(() => {
      setRestrictPopUpSave(false);
      
      if (facilityProcessInfo && facilityProcessInfo.length > 0) {
        setScSubsequentFacilityButtonFlag(true);
      } else if (facilityData?.iId) {
        setScSubsequentFacilityButtonFlag(true);
      } else {
        setScSubsequentFacilityButtonFlag(true);
      }
      
      if (SC_ID) {
        fetchSCStandardDetails();
      }
      
      return () => {
        setRestrictPopUpSave(false);
      }
  }, [])


  const validationSchema = Yup.object().shape({
    FacilityProccessCategoryCode: Yup.string().required("TC Standard is required"),
    FacilityProcessUserSpecificTerm: Yup.string(),
  });

  useEffect(() => {
    const getRawMaterialCodes = async () => {
      try {
        const result: any = await dtrackItService.getFacilityProcessCategoryCodes()
        const rawMaterials = result.data.map((t: any) => ({ value: t.code_1, label:`${t.description_1} (${t.code_1})`}))
        setFacilityProcessCategoryCode(rawMaterials);
        setFacilityProcessCategoryCodes(rawMaterials);
      } catch (err) {
        console.log(`Error in Fetching Organic Farm Standard Code ${err}`)
      }
    };

    getRawMaterialCodes()
  }, [])
  useEffect(() => {
    updateField("facilityProcessDetails", data)
  }, [data])

  const formik = useFormik<FacilityProcessDetails>({
    initialValues: {
      FacilityProccessCategoryCode: "",
      FacilityProcessUserSpecificTerm: "",
    },
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: () => {
      // Submission logic if needed
    },
  });

  const handleCellChange = (
    rowIndex: number,
    columnId: string,
    value: any
  ) => {
    formik.setFieldValue(columnId, value);
    setData((prevData) =>
      prevData.map((row, index) =>
        index === rowIndex ? { ...row, [columnId]: value } : row
      )
    );
  };

  const handleAddNewRow = () => {
    const newRow: FacilityProcessDetails = {
      FacilityProccessCategoryCode: "",
      FacilityProcessUserSpecificTerm: "",
    };
    setRestrictPopUpSave(true);
    const newData = [...data,newRow];
    setData(newData);
    setEditingRowIndex(data.length);
    formik.setValues(newRow);
    formik.setTouched({});
  };

  const handleCancelEdit = async () => {
    // const currentRowValues = formik.values;

    // // Determine if the row being edited is new by comparing `editingRowIndex` with the data length
    // const isNewRow = editingRowIndex === data.length - 1;

    // try {
    //   // Validate the row using the Formik validation schema
    //   await validationSchema.validate(currentRowValues, { abortEarly: false });

    //   if (isNewRow) {
    //     // If validation passes and it's a new row, keep it in the data
    //     setData((prevData) => prevData.map((row, index) => (index === editingRowIndex ? currentRowValues : row)));
    //   } else {
    //     // If it's an existing row, revert changes
    //     formik.setValues(data[editingRowIndex]);
    //   }
    // } catch (validationErrors) {
    //   if (isNewRow) {
    //     // If validation fails and it's a new row, remove it from the data
    //     setData((prevData) => prevData.slice(0, -1));
    //   } else {
    //     // If it's an existing row, revert changes
    //     formik.setValues(data[editingRowIndex]);
    //   }
    // }
    
    setData(tableData);

    // Reset editing state and form
    setRestrictPopUpSave(false);
    setEditingRowIndex(-1);
    formik.resetForm();
  };

  const handleSaveRow = () => {
    if (editingRowIndex === null || !formik.isValid) return;

    const updatedRow = formik.values;
    setSubmissionFlag(updatedRow)
    const message = getValidationMessage(updatedRow);
    setShowWarning(!!message);
    setWarningMessage(message);

    const newData = data?.map((row, index) =>
        index === editingRowIndex ? updatedRow : row
      );
    setData(newData);
    setTableData(newData);
    setFacilityProcessInfo(data); // Update parent with current data
    setRestrictPopUpSave(false);
    setEditingRowIndex(-1);
    formik.resetForm();
  };

  const columns: EditableColumnDef<FacilityProcessDetails>[] = [
    {
      accessorKey: "FacilityProccessCategoryCode",
      header: "Facility Process Category Code",
      Header: <span> <i style={{ color: 'red' }}>* </i>Facility Process Category Code</span>,
      renderEditCell: ({ value, onChange }) => (
        <CustomSelect
          name="FacilityProccessCategoryCode"
          value={value}
          placeholder="Enter value"
          options={facilityProcessCategoryCode}
          enableSearch={true}
          onChange={(e) => {
            onChange(e.target.value)
          }}
        />
      ),
    },
    {
      accessorKey: "FacilityProcessUserSpecificTerm",
      header: "Facility Process Category User Specific Term",
      renderEditCell: ({ value, onChange }) => (
        <CustomTextField
          name="FacilityProcessUserSpecificTerm"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter value"
          sx={{ mb: 2 }}
        />

      ),
    },
  ];

  const handleEditRow = (rowIndex: number) => {
    setEditingRowIndex(rowIndex);
    setRestrictPopUpSave(true);
    formik.setValues(data[rowIndex]);
  };

  const handleDeleteRow = (rowIndex: number) => {
    const updatedData = data.filter((_, index) => index !== rowIndex);
    if (!subsequentFacilityFlag && !scSubsequentFacilityButtonFlag) {
      setShowWarning(false);
      setSubsequentFacilityFlag(true);
      setScSubsequentFacilityButtonFlag(true);
    }
    setData(updatedData);
    setTableData(updatedData);
    setFacilityProcessInfo(updatedData); // Update parent with updated data
  };

  return (
    <div>
      <Box
        component="main"
        sx={{ flexGrow: 1, p: 1, border: "2px solid rgba(128, 128, 128, .2)", my: 4 }}
      >
        <Box display="flex" mb={3} justifyContent="flex-end" width="100%">
          <StyledButton disabled={!formik.isValid || isRestrictPopUpSave} onClick={handleAddNewRow} sx={{ mb: 2 }}>
            Add Facility Process Details
          </StyledButton>
        </Box>
        {showWarning && (
          <Box sx={{ p: 2, mb: 2, bgcolor: '#FFF3E0', borderRadius: 1, border: '1px solid #FFB74D' }}>
            <Typography color="warning.dark">
              {warningMessage}
            </Typography>
          </Box>
        )}
        <EditableTable
          data={data}
          columns={columns}
          onSaveRow={handleSaveRow}
          onCellChange={handleCellChange}
          editingRowIndex={editingRowIndex}
          onEditRow={handleEditRow}
          onCancelEdit={handleCancelEdit}
          onDeleteRow={handleDeleteRow}
          enablePagination={false}
        />
      </Box>
    </div>
  );
};

export default FacilityProcessInfo;
